import Event from '../utils/event.js';

import ActionDragToRegion from './action-drag-to-region.js';
import DragObject from './drag-object.js';
import Region from './region.js';
import Pointer from './pointer.js';

class DragDrop {
    constructor(pointer) {
        this._objects = new Set();
        this._regions = new Set();
        this._pointer = pointer;

        this._dragAction = null;
        this._initialPosition = new Map();
        this._currentPosition = new Map();
        this._objectRegion = new Map();
        this._regionObject = new Map();

        this._pointerDownThis = this._pointerDown.bind(this);
        this._pointerUpThis = this._pointerUp.bind(this);
        this._pointerMoveThis = this._pointerMove.bind(this);
        this._dragObjectEndThis = this._dragObjectEnd.bind(this);

        pointer.downEvent.listen(this._pointerDownThis);
        pointer.upEvent.listen(this._pointerUpThis);
        pointer.moveEvent.listen(this._pointerMoveThis);

        this.enterEvent = new Event(); // { region, object }
        this.resetEvent = new Event(); // { object }
    }

    dispose() {
        this._pointer.downEvent.unlisten(this._pointerDownThis);
        this._pointer.upEvent.unlisten(this._pointerUpThis);
        this._pointer.moveEvent.unlisten(this._pointerMoveThis);

        if (this._dragAction) {
            this._dragAction.cancel();
        }
        delete this._dragAction;

        if (this._objects){
            for (let object of this._objects) {
                this._uninitObject(object);
            }
        }

        delete this._objects;
        delete this._regions;
    }

    addObject(object) {
        if (this._objects.has(object)) {
            throw new Error('already added');
        }

        this._objects.add(object);
        this._initObject(object);
    }

    removeObject(object) {
        if (!this._objects.has(object)) {
            throw new Error('not added');
        }

        this._uninitObject(object);
        this._objects.delete(object);
    }

    addRegion(region) {
        if (this._regions.has(region)) {
            throw new Error('already added');
        }

        this._regions.add(region);
        this._initRegion(region);
    }

    removeRegion(region) {
        if (!this._regions.has(region)) {
            throw new Error('not added');
        }

        this._uninitRegion(region);
        this._regions.delete(region);
    }

    getObjectRegion(object) {
        return this._objectRegion.get(object);
    }

    getRegionObject(region) {
        return this._regionObject.get(region);
    }

    _initObject(object) {
        this._initialPosition.set(object, object.getPosition());
        this._currentPosition.set(object, object.getPosition());
    }

    _uninitObject(object) {
        let region = this._objectRegion.get(object);
        if (region) {
            this._objectRegion.delete(object);
            this._regionObject.delete(region);
        }

        this._initialPosition.delete(object);
        this._currentPosition.delete(object);
    }

    _initRegion(region) {
        
    }

    _uninitRegion(region) {
        let object = this._regionObject.get(region);

        if (object) {
            let { x, y } = this._initialPosition.get(object);
            this._currentPosition.set(object, { x, y });
            object.setPosition({ x, y });
            this._objectRegion.delete(object);
        }

        this._regionObject.delete(region);
    }

    _pointerDown({ position, object }) {
        if (this._dragAction) {
            this._dragAction.finish();
            this._dragAction = null;
        }

        if (!object) return;

        this._dragAction = new ActionDragToRegion(object, this._regions, position);
        this._dragAction.promise.then(this._dragObjectEndThis);
    }

    _pointerUp({ position }) {
        if (!this._dragAction) return;

        this._dragAction.finish();
        this._dragAction = null;
    }

    _pointerMove({ position }) {
        if (!this._dragAction) return;

        this._dragAction.movePointer(position);
    }

    resetPosition(object) {
        let { x, y } = this._initialPosition.get(object);
        object.setPosition({ x, y });
    }

    resetPositions() {
        for (let object of this._objects) {
            this.resetPosition(object);
        }
    }

    _dragObjectEnd({ status, region, object }) {
        switch (status) {
            case 'finished': {
                if (!Q3.editMode && this._regionObject.has(region) && Q3.data.slides[Q3.SlideManager.activeSlide].slideQuiz.use2DSnapping) {
                    object.setPosition(this._currentPosition.get(object));
                    return;
                }

                let oldRegion = this._objectRegion.get(object);
                if (oldRegion) {
                    this._regionObject.delete(oldRegion);
                }

                this._regionObject.set(region, object);
                this._objectRegion.set(object, region);
                this._currentPosition.set(object, object.getPosition());

                this.enterEvent.fire({ region, object });
                break;
            }
            case 'reset': {
                let oldRegion = this._objectRegion.get(object);
                if (oldRegion) {
                    this._regionObject.delete(oldRegion);
                    this._objectRegion.delete(object);

                    let { x, y } = this._initialPosition.get(object);
                    this._currentPosition.set(object, { x, y });
                    object.setPosition({ x, y });

                    this.resetEvent.fire({ object });
                }
                break;
            }
            case 'cancelled':
                break;
            default:
                throw new Error('invalid status: '+status);
        }
    }
}

export default DragDrop;
